import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    role?: string
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'welcome',
    component: () => import('@/views/WelcomePage.vue'),
  },
  {
    path: '/register/customer',
    name: 'register-customer',
    component: () => import('@/views/RegisterCustomer.vue'),
  },
  {
    path: '/register/underwriter',
    name: 'register-underwriter',
    component: () => import('@/views/RegisterUnderwriter.vue'),
  },
  {
    path: '/verify-email',
    name: 'verify-email',
    component: () => import('@/views/VerifyEmail.vue'),
  },
  {
    path: '/onboard',
    name: 'onboard',
    redirect: '/onboard/1',
    component: () => import('@/views/OnboardingFlow.vue'),
    children: [
      {
        path: ':step',
        name: 'onboard-step',
        component: () => import('@/views/OnboardingFlow.vue'),
        props: true,
      },
    ],
  },
  {
    path: '/quote/:id',
    name: 'quote',
    component: () => import('@/views/QuotePage.vue'),
    props: true,
  },
  // Customer-only routes
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/CustomerDashboard.vue'),
    meta: { requiresAuth: true, role: 'customer' },
  },
  {
    path: '/policy/:id',
    name: 'policy',
    component: () => import('@/views/PolicyDetail.vue'),
    props: true,
    meta: { requiresAuth: true, role: 'customer' },
  },
  {
    path: '/policy/:id/sign',
    name: 'policy-sign',
    component: () => import('@/views/PolicySign.vue'),
    props: true,
    meta: { requiresAuth: true, role: 'customer' },
  },
  {
    path: '/policy/:id/payment',
    name: 'policy-payment',
    component: () => import('@/views/PolicyPayment.vue'),
    props: true,
    meta: { requiresAuth: true, role: 'customer' },
  },
  {
    path: '/claim/:id',
    name: 'claim',
    component: () => import('@/views/ClaimDetail.vue'),
    props: true,
    meta: { requiresAuth: true, role: 'customer' },
  },
  // Customer policy request detail
  {
    path: '/request/:id',
    name: 'request-detail',
    component: () => import('@/views/PolicyRequestDetail.vue'),
    props: true,
    meta: { requiresAuth: true, role: 'customer' },
  },
  // Underwriter-only routes
  {
    path: '/underwriter',
    name: 'underwriter',
    component: () => import('@/views/UnderwriterDashboard.vue'),
    meta: { requiresAuth: true, role: 'underwriter' },
  },
  {
    path: '/underwriter/policies',
    name: 'underwriter-policies',
    component: () => import('@/views/UnderwriterPolicies.vue'),
    meta: { requiresAuth: true, role: 'underwriter' },
  },
  {
    path: '/underwriter/policy/:id',
    name: 'underwriter-policy',
    component: () => import('@/views/UnderwriterPolicyDetail.vue'),
    props: true,
    meta: { requiresAuth: true, role: 'underwriter' },
  },
  {
    path: '/underwriter/claims',
    name: 'underwriter-claims',
    component: () => import('@/views/UnderwriterClaims.vue'),
    meta: { requiresAuth: true, role: 'underwriter' },
  },
  {
    path: '/underwriter/claim/:id',
    name: 'underwriter-claim',
    component: () => import('@/views/UnderwriterClaimDetail.vue'),
    props: true,
    meta: { requiresAuth: true, role: 'underwriter' },
  },
  {
    path: '/underwriter/map',
    name: 'underwriter-map',
    component: () => import('@/views/UnderwriterMap.vue'),
    meta: { requiresAuth: true, role: 'underwriter' },
  },
  {
    path: '/underwriter/request/:id',
    name: 'underwriter-request',
    component: () => import('@/views/UnderwriterRequestDetail.vue'),
    props: true,
    meta: { requiresAuth: true, role: 'underwriter' },
  },
  {
    path: '/underwriter/settings',
    name: 'underwriter-settings',
    component: () => import('@/views/UnderwriterSettings.vue'),
    meta: { requiresAuth: true, role: 'underwriter' },
  },
  // Admin-only routes
  {
    path: '/admin',
    name: 'admin',
    component: () => import('@/views/AdminDashboard.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/users',
    name: 'admin-users',
    component: () => import('@/views/AdminUsers.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/simulations',
    name: 'admin-simulations',
    component: () => import('@/views/AdminSimulations.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

/** Return the home route name for the user's primary role */
function homeForRole(role?: string): string {
  if (role === 'admin') return 'admin'
  if (role === 'underwriter') return 'underwriter'
  return 'dashboard'
}

router.beforeEach((to, _from, next) => {
  const auth = useAuthStore()

  // While Keycloak hasn't initialized, only allow public pages (welcome, onboard, quote)
  if (!auth.initialized) {
    const publicNames = ['welcome', 'onboard', 'onboard-step', 'quote', 'register-customer', 'register-underwriter', 'verify-email']
    if (to.name && publicNames.includes(to.name as string)) {
      return next()
    }
    // For protected routes, redirect to welcome (Keycloak will redirect back after login)
    if (to.meta.requiresAuth) {
      return next({ name: 'welcome' })
    }
    return next()
  }

  // Require authentication
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return next({ name: 'welcome' })
  }

  // Enforce role — use primaryRole (admin, underwriter, or customer) not roles array
  // This ensures admin (who also has underwriter Keycloak role) can only access admin routes
  if (to.meta.role && auth.isAuthenticated) {
    const requiredRole = to.meta.role as string
    const primaryRole = auth.user?.role
    if (primaryRole !== requiredRole) {
      return next({ name: homeForRole(primaryRole) })
    }
  }

  // Redirect welcome page to user's home based on primary role
  if (to.name === 'welcome' && auth.isAuthenticated) {
    return next({ name: homeForRole(auth.user?.role) })
  }

  next()
})

export default router
